import { useState } from 'react';
import { tiers } from '../config/plans';
import api from '../utils/api';
import analytics from '../utils/analytics';
import NotificationToast from './NotificationToast';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ConfirmPlanChangeDialog from './ConfirmPlanChangeDialog';

export default function ManageSubscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPlan = JSON.parse(decodeURIComponent(searchParams.get('plan')));
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isUpgrade, setIsUpgrade] = useState(false);

  const handlePlanChange = async (planId, isUpgrade) => {
    setIsLoading(true);
    try {
      const endpoint = isUpgrade ? 'upgrade' : 'downgrade';
      await api.changePlan(planId, endpoint);
      analytics.track('Change subscription', {
        'action': isUpgrade ? 'upgrade' : 'downgrade',
        'plan_id': planId
      });
      window.location.href = '/billing?success=true';
    } catch (error) {
      setErrorMsg(error.message);
      setShowErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanChangeClick = (tier, isUpgrade) => {
    setSelectedPlan(tier);
    setIsUpgrade(isUpgrade);
    setDialogOpen(true);
  };

  const handlePlanChangeConfirm = async () => {
    setDialogOpen(false);
    await handlePlanChange(selectedPlan.productId, isUpgrade);
  };

  const getPlanButton = (tier) => {
    if (tier.name === currentPlan.name) {
      return (
        <button
          disabled
          className="mt-8 block w-full bg-gray-300 border border-gray-400 rounded-md py-2 text-sm font-semibold text-white text-center"
        >
          Your Plan
        </button>
      );
    }

    const isUpgrade = tiers.findIndex(t => t.name === tier.name) > 
                      tiers.findIndex(t => t.name === currentPlan.name);

    const isEnterprisePlan = tier.name === 'Enterprise Plus' || tier.name === 'Enterprise Scale';
    const buttonClasses = isEnterprisePlan 
      ? "mt-8 block w-full bg-yellow-600 border border-yellow-700 rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-yellow-700"
      : "mt-8 block w-full bg-mypurple border border-myviolet rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-gray-900";

    return (
      <button
        disabled={isLoading}
        onClick={() => handlePlanChangeClick(tier, isUpgrade)}
        className={buttonClasses}
      >
        {isUpgrade ? 'Upgrade to' : 'Downgrade to'} {tier.name}
      </button>
    );
  };

  return (
    <div className="bg-white relative">
      <button
        onClick={() => navigate('/billing')}
        className="absolute top-8 right-8 p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Close"
      >
        <XMarkIcon className="h-8 w-8 text-gray-500 hover:text-gray-700" />
      </button>

      <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:flex-col sm:align-center">
          <h1 className="text-5xl font-extrabold text-gray-900 sm:text-center">Manage Subscription</h1>
          <p className="mt-5 text-xl text-gray-500 sm:text-center">
            Change your subscription plan
          </p>
        </div>

        <NotificationToast 
          show={showErrorToast} 
          setShow={setShowErrorToast}
          notifMsg={errorMsg}
          notifType="error"
        />

        <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0 xl:grid-cols-4">
          {tiers.slice(0, 4).map((tier) => (
            <div key={tier.name} className="border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200">
              <div className="p-6">
                <h2 className="text-lg leading-6 font-medium text-gray-900">{tier.name}</h2>
                <p className="mt-4 text-sm text-gray-500">{tier.description}</p>
                <p className="mt-8">
                  <span className="text-4xl font-extrabold text-gray-900">${tier.priceMonthly}</span>
                  <span className="text-base font-medium text-gray-500">/mo</span>
                </p>
                {getPlanButton(tier)}
              </div>
              <div className="pt-6 pb-8 px-6">
                <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">What's included</h3>
                <ul className="mt-6 space-y-4">
                  {tier.includedFeatures.map((feature) => (
                    <li key={feature} className="flex space-x-3">
                      <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                      <span className="text-sm text-gray-500">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Enterprise Plus Plans */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-6">
          {tiers.slice(4).map((tier) => (
            <div key={tier.name} className="border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200">
              <div className="p-6">
                <h2 className="text-lg leading-6 font-medium text-gray-900">{tier.name}</h2>
                <p className="mt-4 text-sm text-gray-500">{tier.description}</p>
                <p className="mt-8">
                  <span className="text-4xl font-extrabold text-gray-900">${tier.priceMonthly}</span>
                  <span className="text-base font-medium text-gray-500">/mo</span>
                </p>
                {getPlanButton(tier)}
              </div>
              <div className="pt-6 pb-8 px-6">
                <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">What's included</h3>
                <ul className="mt-6 space-y-4">
                  {tier.includedFeatures.map((feature) => (
                    <li key={feature} className="flex space-x-3">
                      <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                      <span className="text-sm text-gray-500">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>

      <ConfirmPlanChangeDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        planName={selectedPlan?.name}
        isUpgrade={isUpgrade}
        onConfirm={handlePlanChangeConfirm}
      />
    </div>
  );
}
